<div class="container">
  <div class="columns is-centered">
    <div class="column is-10-tablet is-8-desktop">

      @if (submitted()) {
        <div class="box has-text-centered py-6">
          <h1 class="title is-3">Registration complete</h1>
          <p class="mb-4">
            Your account has been created successfully. You can now sign in to the
            Hiroshima AI Process Reporting Framework portal.
          </p>
          <a routerLink="/login" class="button is-link">Sign in</a>
        </div>
      } @else {

        <h1 class="title is-3 has-text-weight-light mb-5">
          Create an account
        </h1>

        <app-step-progress-bar
          [steps]="steps"
          [currentStep]="currentStep"
          (stepClicked)="goToStep($event)"
        ></app-step-progress-bar>

        @if (errorMessage()) {
          <div class="notification is-danger is-light">
            {{ errorMessage() }}
          </div>
        }

        <div class="box p-5">
          <form [formGroup]="form">

            <!-- Step 1: Account -->
            @if (currentStep === 1) {
              <div class="columns is-multiline is-variable is-3">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">First name *</label>
                    <div class="control">
                      <input
                        class="input"
                        type="text"
                        formControlName="firstName"
                        [class.is-danger]="form.get('firstName')!.touched && form.get('firstName')!.invalid"
                      />
                    </div>
                    @if (form.get('firstName')!.touched && form.get('firstName')!.invalid) {
                      <p class="help is-danger">First name is required</p>
                    }
                  </div>
                </div>

                <div class="column is-6">
                  <div class="field">
                    <label class="label">Last name *</label>
                    <div class="control">
                      <input
                        class="input"
                        type="text"
                        formControlName="lastName"
                        [class.is-danger]="form.get('lastName')!.touched && form.get('lastName')!.invalid"
                      />
                    </div>
                    @if (form.get('lastName')!.touched && form.get('lastName')!.invalid) {
                      <p class="help is-danger">Last name is required</p>
                    }
                  </div>
                </div>

                <div class="column is-12">
                  <div class="field">
                    <label class="label">Work email *</label>
                    <div class="control">
                      <input
                        class="input"
                        type="email"
                        formControlName="email"
                        [class.is-danger]="form.get('email')!.touched && form.get('email')!.invalid"
                      />
                    </div>
                    @if (form.get('email')!.touched && form.get('email')!.invalid) {
                      <p class="help is-danger">A valid email address is required</p>
                    }
                  </div>
                </div>

                <div class="column is-12">
                  <div class="field">
                    <label class="label">Password *</label>
                    <div class="control">
                      <input
                        class="input"
                        type="password"
                        formControlName="password"
                        [class.is-danger]="form.get('password')!.touched && form.get('password')!.invalid"
                      />
                    </div>
                    <p class="help">
                      Min. 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character
                    </p>
                    @if (form.get('password')!.touched && form.get('password')!.invalid) {
                      <p class="help is-danger">Password does not meet the requirements</p>
                    }
                  </div>
                </div>

                <div class="column is-12">
                  <div class="field">
                    <label class="label">Confirm password *</label>
                    <div class="control">
                      <input
                        class="input"
                        type="password"
                        formControlName="confirmPassword"
                        [class.is-danger]="form.get('confirmPassword')!.touched && (form.get('confirmPassword')!.invalid || form.hasError('passwordsMismatch'))"
                      />
                    </div>
                    @if (form.get('confirmPassword')!.touched && form.get('confirmPassword')!.invalid) {
                      <p class="help is-danger">Please confirm your password</p>
                    } @else if (form.get('confirmPassword')!.touched && form.hasError('passwordsMismatch')) {
                      <p class="help is-danger">Passwords do not match</p>
                    }
                  </div>
                </div>
              </div>

              <div class="data-protection-notice mt-5">
                <p class="has-text-weight-semibold notice-title">Data protection notice</p>
                  <p>
                    <span>Personal Data Protection notice for the Hiroshima AI Process Reporting Framework portal for account creation.</span>
                  </p>
                  <p>
                    <span>The OECD is committed to protecting the personal data it processes, in accordance with its personal data protection
                      <a href="https://www.oecd.org/en/about/data-protection.html" target="_blank" rel="noopener noreferrer">Rules</a>.
                    </span>
                  </p>
                  <p>
                    <span>The OECD Directorate for Science Technology and Innovation is using this portal to collect personal data, including First name, Last name, Professional email, Password and Job title. The data will be used to authenticate the responses provided.</span>
                  </p>
                  <p>
                    <span>The personal data we collect will be transferred from the website host (Heroku), stored at the OECD, and retained for the duration of the project. Only OECD project staff have access to your data.</span>
                  </p>
                  <p>
                    <span>Under the
                      <a href="https://www.oecd.org/en/about/data-protection.html" target="_blank" rel="noopener noreferrer">Rules</a>,
                      you have rights to access and rectify your personal data, as well as to object to its processing, request erasure, and obtain data portability in certain circumstances. To exercise these rights in connection with this portal please contact
                      <a href="mailto:ai&#64;oecd.org">ai&#64;oecd.org</a>.
                    </span>
                  </p>
                  <p>
                    <span>If you have further queries or complaints related to the processing of your personal data, please contact the
                      <a href="mailto:dpo&#64;oecd.org">Data Protection Officer</a>.
                      If you need further assistance in resolving claims related to personal data protection you can contact the
                      <a href="mailto:dpc&#64;oecd.org">Data Protection Commissioner</a>.
                    </span>
                  </p>
              </div>

              <button
                type="button"
                class="button is-link is-fullwidth mt-5"
                (click)="continueToNext()"
              >Continue</button>

              <p class="has-text-centered mt-4">
                Already have an account?
                <a routerLink="/login">Sign in instead</a>
              </p>
            }

            <!-- Step 2: Organization -->
            @if (currentStep === 2) {
              <div class="columns is-multiline is-variable is-3">
                <div class="column is-12">
                  <div class="field">
                    <label class="label">Organization name *</label>
                    <div class="control">
                      <input
                        class="input"
                        type="text"
                        formControlName="organizationName"
                        [class.is-danger]="form.get('organizationName')!.touched && form.get('organizationName')!.invalid"
                      />
                    </div>
                    @if (form.get('organizationName')!.touched && form.get('organizationName')!.invalid) {
                      <p class="help is-danger">Organization name is required</p>
                    }
                  </div>
                </div>

                <div class="column is-12">
                  <div class="field">
                    <label class="label">Headquarters country *</label>
                    <div class="control">
                      <div class="select is-fullwidth" [class.is-danger]="form.get('country')!.touched && form.get('country')!.invalid">
                        <select formControlName="country">
                          <option value="" disabled>Select a country</option>
                          @for (country of countries; track country.value) {
                            <option [value]="country.value">{{ country.label }}</option>
                          }
                        </select>
                      </div>
                    </div>
                    <p class="help">
                      Organisations must be based in a jurisdiction that meets at
                      least one of the following conditions: is a member country of
                      the
                      <a href="https://www.oecd.org/en/about/members-partners.html" target="_blank" rel="noopener noreferrer">OECD</a>;
                      is a member of the
                      <a href="https://oecd.ai/en/about/about-gpai" target="_blank" rel="noopener noreferrer">Global Partnership on AI</a>
                      (GPAI); is an
                      <a href="https://www.oecd.org/en/topics/ai-principles.html" target="_blank" rel="noopener noreferrer">Adherent</a>
                      to the OECD Recommendation on Artificial Intelligence.
                    </p>
                    @if (form.get('country')!.touched && form.get('country')!.invalid) {
                      <p class="help is-danger">Country is required</p>
                    }
                  </div>
                </div>

                <div class="column is-12">
                  <div class="field">
                    <label class="label">Address line 1</label>
                    <div class="control">
                      <input class="input" type="text" formControlName="addressLine1" />
                    </div>
                  </div>
                </div>

                <div class="column is-12">
                  <div class="field">
                    <label class="label">Address line 2</label>
                    <div class="control">
                      <input class="input" type="text" formControlName="addressLine2" />
                    </div>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="field">
                    <label class="label">City</label>
                    <div class="control">
                      <input class="input" type="text" formControlName="city" />
                    </div>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="field">
                    <label class="label">State / Province</label>
                    <div class="control">
                      <input class="input" type="text" formControlName="state" />
                    </div>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="field">
                    <label class="label">Postal code</label>
                    <div class="control">
                      <input class="input" type="text" formControlName="zip" />
                    </div>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="field">
                    <label class="label">Job title</label>
                    <div class="control">
                      <input class="input" type="text" formControlName="jobTitle" />
                    </div>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="field">
                    <label class="label">Phone number</label>
                    <div class="control">
                      <input class="input" type="tel" formControlName="phoneNumber" />
                    </div>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="field">
                    <label class="label">Website *</label>
                    <div class="control">
                      <input
                        class="input"
                        type="url"
                        formControlName="website"
                        placeholder="https://www.example.com"
                        (blur)="onWebsiteBlur()"
                        [class.is-danger]="form.get('website')!.touched && form.get('website')!.invalid"
                      />
                    </div>
                    @if (form.get('website')!.touched && form.get('website')!.invalid) {
                      <p class="help is-danger">A valid URL starting with http:// or https:// is required</p>
                    }
                  </div>
                </div>
              </div>

              <div class="is-flex is-justify-content-space-between mt-5">
                <button
                  type="button"
                  class="button"
                  (click)="goToStep(1)"
                >Previous</button>
                <button
                  type="button"
                  class="button is-link"
                  (click)="continueToNext()"
                >Continue</button>
              </div>
            }

            <!-- Step 3: Summary -->
            @if (currentStep === 3) {
              <div class="summary-section has-background-light p-4 mb-4">
                <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                  <h3 class="title is-6 mb-0">Account</h3>
                  <a class="is-size-7 has-text-link" (click)="goToStep(1)">Edit</a>
                </div>
                <div class="columns is-multiline">
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">First name</p>
                    <p>{{ form.get('firstName')!.value }}</p>
                  </div>
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Last name</p>
                    <p>{{ form.get('lastName')!.value }}</p>
                  </div>
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Work email</p>
                    <p>{{ form.get('email')!.value }}</p>
                  </div>
                </div>
              </div>

              <div class="summary-section has-background-light p-4 mb-4">
                <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                  <h3 class="title is-6 mb-0">Organization</h3>
                  <a class="is-size-7 has-text-link" (click)="goToStep(2)">Edit</a>
                </div>
                <div class="columns is-multiline">
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Organization name</p>
                    <p>{{ form.get('organizationName')!.value }}</p>
                  </div>
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Headquarters country</p>
                    <p>{{ getCountryLabel(form.get('country')!.value) }}</p>
                  </div>
                  @if (form.get('addressLine1')!.value) {
                    <div class="column is-6">
                      <p class="has-text-grey is-size-7">Address line 1</p>
                      <p>{{ form.get('addressLine1')!.value }}</p>
                    </div>
                  }
                  @if (form.get('addressLine2')!.value) {
                    <div class="column is-6">
                      <p class="has-text-grey is-size-7">Address line 2</p>
                      <p>{{ form.get('addressLine2')!.value }}</p>
                    </div>
                  }
                  @if (form.get('city')!.value) {
                    <div class="column is-6">
                      <p class="has-text-grey is-size-7">City</p>
                      <p>{{ form.get('city')!.value }}</p>
                    </div>
                  }
                  @if (form.get('state')!.value) {
                    <div class="column is-6">
                      <p class="has-text-grey is-size-7">State / Province</p>
                      <p>{{ form.get('state')!.value }}</p>
                    </div>
                  }
                  @if (form.get('zip')!.value) {
                    <div class="column is-6">
                      <p class="has-text-grey is-size-7">Postal code</p>
                      <p>{{ form.get('zip')!.value }}</p>
                    </div>
                  }
                  @if (form.get('jobTitle')!.value) {
                    <div class="column is-6">
                      <p class="has-text-grey is-size-7">Job title</p>
                      <p>{{ form.get('jobTitle')!.value }}</p>
                    </div>
                  }
                  @if (form.get('phoneNumber')!.value) {
                    <div class="column is-6">
                      <p class="has-text-grey is-size-7">Phone number</p>
                      <p>{{ form.get('phoneNumber')!.value }}</p>
                    </div>
                  }
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Website</p>
                    <p>{{ form.get('website')!.value }}</p>
                  </div>
                </div>
              </div>

              <div class="is-flex is-justify-content-space-between mt-5">
                <button
                  type="button"
                  class="button"
                  (click)="goToStep(2)"
                >Previous</button>
                <button
                  type="button"
                  class="button is-link"
                  [class.is-loading]="loading()"
                  [disabled]="loading()"
                  (click)="submit()"
                >Submit</button>
              </div>
            }
          </form>
        </div>

        <p class="has-text-right is-size-7 mt-2">
          All fields marked with * are required
        </p>
      }
    </div>
  </div>
</div>
